/** Shapes of the per-region content the simulation draws from. Content is data, not code. */

export type Stage = 'young' | 'adult';
export type Sex = 'm' | 'f';
export type Skill = 'academics' | 'technical' | 'business' | 'social' | 'creativity' | 'fitness' | 'cooking' | 'music';
export const SKILLS: Skill[] = ['academics', 'technical', 'business', 'social', 'creativity', 'fitness', 'cooking', 'music'];
export type EduLevel = 'secondary' | 'student' | 'diploma' | 'degree' | 'masters';
export const EDU_RANK: Record<EduLevel, number> = { secondary: 0, student: 1, diploma: 1, degree: 2, masters: 3 };

export interface Background {
  id: string;
  label: string;
  weight: number;
  /** starting savings range (local currency) */
  money: [number, number];
  /** 0..1: how likely higher education is */
  schooling: number;
  homes: [string, number][];
  /** one line about the family */
  family: string[];
}

export interface Home {
  id: string;
  label: string;
  area: string[];
  /** yearly rent paid by the player (0 = family house or owned) */
  rentYearly: [number, number];
  note: string;
}

export interface Job {
  id: string;
  label: string;
  place: string[];
  /** monthly income range */
  income: [number, number];
  stages: Stage[];
  minEdu: EduLevel;
  /** only for these education fields, if set */
  fields?: string[];
  weight: number;
  /** extra weight by background id */
  bg?: Record<string, number>;
  skills: Partial<Record<Skill, number>>;
  sex?: Sex;
}

export interface NameGroup { id: string; weight: number; m: string[]; f: string[]; surname: string[];
  /** written family name first (Chinese: "Wang Lei"); a wife keeps her own family name, the children take the father's */
  surnameFirst?: boolean }

export interface Opportunity {
  text: string;
  /** when it applies */
  when: (l: { stage: Stage; edu: EduLevel; field: string | null; job: string | null; bg: string; skills: Record<Skill, number> }) => boolean;
  weight: number;
}

/** When a job is worked and how it pays. Days: 0 = Monday. */
export interface Schedule { days: number[]; start: number; end: number; pay: 'monthly' | 'daily' | 'none' }

export interface RegionContent {
  /** shift pattern per job id ('default' for anything not listed) */
  schedules: Record<string, Schedule>;
  /** monthly support from family by background, while you live with them */
  allowance: Record<string, number>;
  /** what things cost day to day */
  prices: { transport: number };
  id: string;
  city: string;
  /** what money is shown in: `rate` internal units (the sim counts in naira) per one of these */
  currency: { symbol: string; code: string; rate?: number };
  backgrounds: Background[];
  homes: Home[];
  fields: { id: string; label: string; skills: Partial<Record<Skill, number>> }[];
  institutions: { student: string[]; diploma: string[]; degree: string[]; private?: string[] };
  /** jobs where you have a boss (for opportunities about the workplace) */
  employed?: string[];
  jobs: Job[];
  opportunities: Opportunity[];
  /** naming traditions; a family shares one */
  names: NameGroup[];
  siblings: [number, number][];
  /** parts of the region: a job's place is picked from the same part as the home when possible */
  areaGroups?: RegExp[];
  /** what a monthly income rents you when you move out (internal units): a one-bedroom, a studio, else the cheapest */
  homeTiers?: { oneBed: number; studio: number; youngMoveOut: number; youngOneBed: number; cheapest: string; richAlone: number };
}
